import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const DoorDrawerQualityInspectionChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [priorToFittingCheckOne, setPriorToFittingCheckOne] = useState<
    Checklist[]
  >([]);
  const [allDoorsAndDrawers, setAllDoorsAndDrawers] = useState<Checklist[]>([]);
  const [priorToFittingCheckTwo, setPriorToFittingCheckTwo] = useState<
    Checklist[]
  >([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "door-drawer-quality-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setPriorToFittingCheckOne([]);
        setAllDoorsAndDrawers([]);
        setPriorToFittingCheckTwo([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_doors_drawers_quality_inspection/${chassisNo}`
        );
        const data = response.data;

        const priorOneData = (data.priorToFittingCheckOne || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const doorsDrawersData = (data.allDoorsAndDrawers || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const priorTwoData = (data.priorToFittingCheckTwo || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );

        setPriorToFittingCheckOne(priorOneData);
        setAllDoorsAndDrawers(doorsDrawersData);
        setPriorToFittingCheckTwo(priorTwoData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...priorOneData,
          ...doorsDrawersData,
          ...priorTwoData,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status === "pass" ||
              item.status === "fail" ||
              item.status === "n/a"
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error: any) {
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [
        ...priorToFittingCheckOne,
        ...allDoorsAndDrawers,
        ...priorToFittingCheckTwo,
      ];

      if (isRadioChanged) {
        const isComplete =
          allChecklists.length > 0 &&
          allChecklists.every(
            (item) =>
              item.status === "pass" ||
              item.status === "fail" ||
              item.status === "n/a"
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo,
        model: model,
        priorToFittingCheckOne: priorToFittingCheckOne.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        allDoorsAndDrawers: allDoorsAndDrawers.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        priorToFittingCheckTwo: priorToFittingCheckTwo.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_doors_drawers_quality_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...priorToFittingCheckOne,
          ...allDoorsAndDrawers,
          ...priorToFittingCheckTwo,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr =
              item.notes !== null && item.notes !== undefined ? item.notes : "";

            if (notesStr.trim()) {
              if (notesStr.includes(";")) {
                const parts = notesStr
                  .split(";")
                  .map((part) => part.trim())
                  .filter((part) => part);
                return parts.map((note) => ({
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${note}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                }));
              } else {
                return [
                  {
                    itemNo: itemNoCounter++,
                    problemDescription: `${title}: ${notesStr}`,
                    comment: "",
                    whoFoundFault: checkedBy,
                    responsible: "",
                    whoDidRework: "",
                    timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                    completed: false,
                  },
                ];
              }
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: title,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "door-drawer-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "door-drawer-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (isDisabled && completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "prior_to_fitting_one") {
      updateChecklist(priorToFittingCheckOne, setPriorToFittingCheckOne);
    }
    if (type === "all_doors_and_drawers") {
      updateChecklist(allDoorsAndDrawers, setAllDoorsAndDrawers);
    }
    if (type === "prior_to_fitting_two") {
      updateChecklist(priorToFittingCheckTwo, setPriorToFittingCheckTwo);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (isDisabled && completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "prior_to_fitting_one") {
      updateChecklist(priorToFittingCheckOne, setPriorToFittingCheckOne);
    }
    if (type === "all_doors_and_drawers") {
      updateChecklist(allDoorsAndDrawers, setAllDoorsAndDrawers);
    }
    if (type === "prior_to_fitting_two") {
      updateChecklist(priorToFittingCheckTwo, setPriorToFittingCheckTwo);
    }
  };

  const allChecklists = [
    ...priorToFittingCheckOne,
    ...allDoorsAndDrawers,
    ...priorToFittingCheckTwo,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  console.log("All Checklists:", allChecklists);
  console.log("allNotChecked:", allNotChecked);

  const checklistsWithSerialNumbers = [
    {
      data: priorToFittingCheckOne,
      type: "prior_to_fitting_one",
      subHeaderText: "Prior to Fitting: Check",
    },
    {
      data: allDoorsAndDrawers,
      type: "all_doors_and_drawers",
      subHeaderText: "All Doors & Drawers, including Pantry",
    },
    {
      data: priorToFittingCheckTwo,
      type: "prior_to_fitting_two",
      subHeaderText: "Prior to Fitting: Check",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={isDisabled && completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"door-drawer-quality-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (isDisabled && completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (isDisabled && completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default DoorDrawerQualityInspectionChecklist;
